import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
// src/routes/Lineup.tsx
//
// Phase 4 §4.9 — Drag-and-drop lineup editor.
//
// The deployed `weeklyLineups` schema stores one row per (franchise, season,
// week, slot). slot is a free-form string; we use the canonical names from
// the plan (`QB`, `RB1`, `RB2`, `WR1`–`WR3`, `TE`, `FLEX`, `K`, `DEF`,
// `BENCH1`–`BENCH15`). Locked rows have `locked=true` (set by the kickoff
// cron); the editor refuses to drag them and `setLineup` re-validates.
import { useMutation, useQuery } from 'convex/react';
import { useMemo, useState } from 'react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

const STARTER_SLOTS = ['QB', 'RB1', 'RB2', 'WR1', 'WR2', 'WR3', 'TE', 'FLEX', 'K', 'DEF'] as const;
const BENCH_COUNT = 15;
const BENCH_SLOTS = Array.from({ length: BENCH_COUNT }, (_, i) => `BENCH${i + 1}`);

type SlotAssignment = { slot: string; playerId: Id<'players'> | null };

export default function Lineup() {
  const me = useQuery(api.franchises.myFranchise);
  const currentSeason = useQuery(api.seasons.getCurrent);
  const week = useQuery(
    api.schedule.getCurrentWeek,
    currentSeason ? { season: currentSeason.year } : 'skip',
  );
  const lineup = useQuery(
    api.lineups.getLineup,
    me && currentSeason && week !== undefined
      ? { franchiseId: me._id, week, season: currentSeason.year }
      : 'skip',
  );
  const lockedIds = useQuery(
    api.lineups.getLockedPlayers,
    me && currentSeason && week !== undefined
      ? { franchiseId: me._id, week, season: currentSeason.year }
      : 'skip',
  );
  const setLineup = useMutation(api.lineups.setLineup);
  const sensors = useSensors(useSensor(PointerSensor));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingSlots, setPendingSlots] = useState<SlotAssignment[] | null>(null);

  const slotState = useMemo<SlotAssignment[]>(() => {
    if (pendingSlots) return pendingSlots;
    const all: SlotAssignment[] = [
      ...STARTER_SLOTS.map((s) => ({ slot: s as string, playerId: null as Id<'players'> | null })),
      ...BENCH_SLOTS.map((s) => ({ slot: s, playerId: null as Id<'players'> | null })),
    ];
    if (!lineup) return all;
    for (const row of lineup) {
      const idx = all.findIndex((s) => s.slot === row.slot);
      if (idx >= 0) all[idx].playerId = row.playerId ?? null;
    }
    return all;
  }, [lineup, pendingSlots]);

  if (me === undefined) return <div className="p-6">Loading…</div>;
  if (me === null) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">No franchise</h1>
        <p className="mt-2 text-sm text-gray-600">
          Claim a franchise first.{' '}
          <a className="underline" href="/franchises">
            Pick one →
          </a>
        </p>
      </div>
    );
  }
  if (!currentSeason || week === undefined || lineup === undefined) {
    return <div className="p-6">Loading lineup…</div>;
  }

  const lockedSet = new Set<string>((lockedIds ?? []).map((id: Id<'players'>) => String(id)));

  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over) return;
    const fromSlot = String(e.active.id);
    const toSlot = String(e.over.id);
    if (fromSlot === toSlot) return;
    const next = slotState.map((s) => ({ ...s }));
    const fromIdx = next.findIndex((s) => s.slot === fromSlot);
    const toIdx = next.findIndex((s) => s.slot === toSlot);
    if (fromIdx < 0 || toIdx < 0) return;
    // Refuse to disturb locked players.
    const movingPlayer = next[fromIdx].playerId;
    const displacedPlayer = next[toIdx].playerId;
    if (movingPlayer && lockedSet.has(String(movingPlayer))) return;
    if (displacedPlayer && lockedSet.has(String(displacedPlayer))) return;
    next[fromIdx].playerId = displacedPlayer;
    next[toIdx].playerId = movingPlayer;
    setPendingSlots(next);
  };

  const onSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const payload = slotState
        .filter((s) => s.playerId !== null)
        .map((s) => ({ slot: s.slot, playerId: s.playerId as Id<'players'> }));
      await setLineup({ week, season: currentSeason.year, slots: payload });
      setPendingSlots(null); // reload from server subscription
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const dirty = pendingSlots !== null;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <header className="mb-4">
        <h1 className="text-2xl font-bold">Week {week} Lineup</h1>
        <p className="text-sm text-gray-600 mt-1">
          Drag a player onto another slot to swap. Locked players (their NFL game has kicked off)
          cannot be moved.
        </p>
      </header>
      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 mb-3">
          {error}
        </div>
      )}
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="grid gap-6 md:grid-cols-2">
          <Section title="Starters">
            {STARTER_SLOTS.map((slot) => {
              const playerId = slotState.find((s) => s.slot === slot)?.playerId ?? null;
              const locked = playerId !== null && lockedSet.has(String(playerId));
              return <SlotRow key={slot} slot={slot} playerId={playerId} locked={locked} />;
            })}
          </Section>
          <Section title="Bench">
            {BENCH_SLOTS.map((slot) => {
              const playerId = slotState.find((s) => s.slot === slot)?.playerId ?? null;
              const locked = playerId !== null && lockedSet.has(String(playerId));
              return <SlotRow key={slot} slot={slot} playerId={playerId} locked={locked} />;
            })}
          </Section>
        </div>
      </DndContext>
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Lineup'}
        </button>
        {dirty && (
          <button
            type="button"
            onClick={() => setPendingSlots(null)}
            disabled={saving}
            className="px-4 py-2 border rounded text-sm disabled:opacity-50"
          >
            Discard changes
          </button>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-semibold mb-2">{title}</h2>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function SlotRow({
  slot,
  playerId,
  locked,
}: {
  slot: string;
  playerId: Id<'players'> | null;
  locked: boolean;
}) {
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: slot, disabled: locked });
  return (
    <div
      ref={setDropRef}
      className={`flex items-center border rounded p-2 bg-white ${isOver ? 'bg-blue-50' : ''} ${
        locked ? 'opacity-60' : ''
      }`}
    >
      <span className="w-16 font-mono text-xs text-gray-500">{slot}</span>
      {playerId ? (
        <DraggablePlayer slot={slot} playerId={playerId} disabled={locked} />
      ) : (
        <span className="text-gray-400 italic text-sm">empty</span>
      )}
      {locked && (
        <span className="ml-auto text-xs uppercase tracking-wide text-red-600">locked</span>
      )}
    </div>
  );
}

function DraggablePlayer({
  slot,
  playerId,
  disabled,
}: {
  slot: string;
  playerId: Id<'players'>;
  disabled: boolean;
}) {
  const player = useQuery(api.players.get, { playerId });
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: slot,
    disabled,
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  return (
    <span
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`ml-3 text-sm ${disabled ? 'cursor-not-allowed' : 'cursor-grab'} ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      {player ? `${player.name} (${player.position} ${player.nflTeam})` : '…'}
    </span>
  );
}
